/**
 * Generates a list of value-color stops for a binned color scale, based on the given min and max values.
 * @param min - Minimum value of the data range.
 * @param max - Maximum value of the data range.
 * @param numStops - Desired number of stops.
 * @param metric - Optional metric name for contextual logic.
 * @returns An array of [number, color] pairs for use in paint and legend bindings.
 */
export function generateStops(
    min: number | null,
    max: number | null,
    metric?: string,
): [number, string][] {
    if (min == null || max == null) {
        return [];
    }
    if (min === max) {
        return [[min, "#00F5F5"]];
    }

    // Adjust number of stops with range and keep it between 2-5.
    var range = max - min;
    let numStops = Math.ceil(Math.log10(range));
    numStops = Math.min(5, Math.max(2, numStops)); // clamp 2�5

    const colorRamp =
        numStops === 10
            ? [
                  "#00F5F5",
                  "#2DC8F7",
                  "#369AF7",
                  "#3674F7",
                  "#2F4DF7",
                  "#1B22F5",
                  "#6200F5",
                  "#8700F5",
                  "#D000F5",
                  "#F500F5",
              ]
            : ["#65E0F9", "#369AF7", "#3100F5", "#AB00F5", "#F500F5"]; // numStops = 5

    var adjustedRamp = colorRamp.slice(0, numStops);

    // The bins are distributed using a power-eased scaling function, which applies right-skewed exponential transformation (inverted exponential (x2)).
    // This creates narrower bins at the lower end and wider bins at the higher end.
    var power = 3; // Power factor

    // Generate eased stops
    var stops: [number, string][] = [];
    for (let i = 0; i < numStops; i++) {
        var t = i / (numStops - 1);
        var scaled = Math.pow(t, power); // Apply easing
        var val = min + scaled * range;

        // Apply nice rounding
        val =
            metric === "tonnes_per_trailer"
                ? Math.round(val)
                : niceRound(val, range, min);

        // If two consecutive stops collapse to the same value, skip one
        if (stops.length === 0 || val > stops[stops.length - 1][0]) {
            stops.push([val, adjustedRamp[i]]);
        }
    }
    return stops;
}

/**
 * A helper function for nice rounding. This removes awkward stop endings and makes legend more readable.
 * @param val - A value (number) to round.
 * @returns A rounded number.
 */
export function niceRound(val: number, range: number, min: number): number {
    if (val === 0) {
        return 1;
    }
    // Step size = order of magnitude of the range, not just the value itself.
    var step = Math.pow(10, Math.floor(Math.log10(range)) - 1);
    // Don't round too much for small ranges.
    var safeStep = Math.max(1, step);
    var rounded = Math.round(val / safeStep) * safeStep;
    // Make sure that it's never rounded below the actual min.
    return min !== undefined ? Math.max(min, rounded) : rounded;
}

/**
 * A helper function to format number for the dynamic legend.
 * @param val - A value (number) to format.
 * @returns A nicely formated number for legend labels.
 */
export function formatDynamicLegendNumber(val: number): string {
    if (val >= 1_000_000_000) {
        return (
            (val / 1_000_000_000).toFixed(val % 1_000_000_000 === 0 ? 0 : 1) +
            "B"
        );
    } else if (val >= 1_000_000) {
        return (val / 1_000_000).toFixed(val % 1_000_000 === 0 ? 0 : 1) + "M";
    }
    return val.toLocaleString();
}

/**
 * Appends appropriate unit symbols or currency signs to formatted legend values based on the metric key.
 *
 * - For 'cost' or 'freight' metrics: Adds `$` before both `from` and `to` values.
 * - For 'distance': Appends `' km'` to the `to` value (or `from` if `to` is null).
 * - For 'duration': Appends `' hrs'` to the `to` value (or `from` if `to` is null).
 * - For 'co2': Appends `' t'` to the `to` value (or `from` if `to` is null).
 *
 * @param key - The metric name used to infer the unit (case-insensitive).
 * @param formattedFrom - Already-formatted lower bound of the range.
 * @param formattedTo - Already-formatted upper bound of the range, or null for open-ended ranges.
 * @returns A tuple of `[formattedFrom, formattedTo]` with appropriate units applied.
 */
export function formatLegendUnits(
    key: string,
    formattedFrom: string,
    formattedTo: string | null,
): [string, string | null] {
    const keyLower = key.toLowerCase();

    if (keyLower.includes("cost") || keyLower.includes("freight")) {
        formattedFrom = `$${formattedFrom}`;
        if (formattedTo !== null) formattedTo = `$${formattedTo}`;
    } else if (keyLower.includes("distance")) {
        formattedTo !== null
            ? (formattedTo += " km")
            : (formattedFrom += " km");
    } else if (keyLower.includes("duration")) {
        formattedTo !== null
            ? (formattedTo += " hrs")
            : (formattedFrom += " hrs");
    } else if (keyLower.includes("co2")) {
        formattedTo !== null ? (formattedTo += " t") : (formattedFrom += " t");
    }
    return [formattedFrom, formattedTo];
}
